import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiEndpoint } from "../../config/config";
import {
  Container,
  TextInput,
  Button,
  Stack,
  Alert,
  Image,
  Box,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import rNameLogo from "../../assets/Reppi.svg";

function AdminTokenLoginPage() {
  const [id, setId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { token } = useParams();

  useEffect(() => {
    // If no token in URL, redirect to login
    if (!token) {
      navigate("/login");
    }
  }, [token, navigate]);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!token) {
      setError("No admin token provided.");
      setLoading(false);
      return;
    }

    if (!id.trim()) {
      setError("Please enter an ID.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(getApiEndpoint("/v1/user/token/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({
          rep_id: id.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok) {
        navigate("/dashboard");
      } else {
        // Login failed
        setError(
          result.message ||
            "Admin token login failed. Please check your token and ID."
        );
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null; // Will redirect to login
  }

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-bg-secondary)",
        padding: "20px",
      }}
    >
      <Container size={500}>
        <Box
          bg="white"
          style={{
            borderRadius: "8px",
            boxShadow: "var(--shadow-md)",
            padding: "60px 40px",
          }}
        >
          <Box
            mb={40}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/home")}
            px="3rem"
          >
            <Image src={rNameLogo} alt="Reppi" h={60} w="auto" mx="auto" />
          </Box>

          <Stack gap="md" mb="lg" align="center">
            <Title order={2} size="h3" ta="center">
              Admin Token Login
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Please enter your ID to complete the login process
            </Text>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Stack gap="lg">
              {error && (
                <Alert
                  icon={<IconAlertCircle size="1rem" />}
                  color="red"
                  variant="light"
                >
                  {error}
                </Alert>
              )}

              <TextInput
                label="ID"
                placeholder="Enter your ID"
                value={id}
                onChange={event => setId(event.currentTarget.value)}
                required
                size="md"
                description="Enter the ID associated with your admin access"
              />

              <Button
                type="submit"
                fullWidth
                size="md"
                loading={loading}
                loaderProps={{ type: "dots" }}
              >
                {loading ? "Logging in..." : "Login with Admin Token"}
              </Button>
            </Stack>
          </form>
        </Box>
      </Container>
    </Box>
  );
}

export default AdminTokenLoginPage;
